import React, { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { RoundedBox } from '@react-three/drei';

// Procedural low-poly modern Southwestern home with better lighting/materials.
// Uses RoundedBox for subtle chamfers → reads far less flat under PBR.
export const House = forwardRef((props, ref) => {
    const progressRef = useRef(0);
    useImperativeHandle(ref, () => ({
        setProgress: (v) => {
            progressRef.current = v;
            const p = THREE.MathUtils.clamp(v, 0, 1);
            const t = THREE.MathUtils.clamp(p / 0.55, 0, 1);
            stuccoRefs.current.forEach((m) => m && m.material && m.material.color.lerpColors(C.stuccoWeathered, C.stuccoFresh, t));
            trimRefs.current.forEach((m) => m && m.material && m.material.color.lerpColors(C.trimWeathered, C.trimFresh, t));
            if (garageRef.current?.material) garageRef.current.material.color.lerpColors(C.garageWeathered, C.garageFresh, t);
            roofRefs.current.forEach((m) => m && m.material && m.material.color.lerpColors(C.roofWeathered, C.roofFresh, t));
            windowRefs.current.forEach((m) => {
                if (m?.material) {
                    m.material.color.lerpColors(C.glassWeathered, C.glassFresh, t);
                    m.material.emissiveIntensity = THREE.MathUtils.lerp(0.05, 0.8, t);
                }
            });
            const doorOpen = THREE.MathUtils.clamp((p - 0.55) / 0.2, 0, 1);
            if (doorPivotRef.current) {
                doorPivotRef.current.rotation.y = THREE.MathUtils.lerp(0, -Math.PI * 0.55, doorOpen);
            }
            archLightRefs.current.forEach((l, i) => {
                if (l) l.intensity = THREE.MathUtils.lerp(0.15, i === 0 ? 2.2 : 1.4, p);
            });
        },
    }));

    const stuccoRefs = useRef([]);
    const trimRefs = useRef([]);
    const garageRef = useRef();
    const doorPivotRef = useRef();
    const windowRefs = useRef([]);
    const roofRefs = useRef([]);
    const archLightRefs = useRef([]);
    const doorPanelRef = useRef();

    // Palette
    const C = useMemo(
        () => ({
            stuccoWeathered: new THREE.Color('#8a8377'),
            stuccoFresh: new THREE.Color('#EDE4D2'),
            trimWeathered: new THREE.Color('#3d3a35'),
            trimFresh: new THREE.Color('#101010'),
            garageWeathered: new THREE.Color('#6b6960'),
            garageFresh: new THREE.Color('#242220'),
            roofWeathered: new THREE.Color('#4a463f'),
            roofFresh: new THREE.Color('#171614'),
            glassWeathered: new THREE.Color('#7d8a95'),
            glassFresh: new THREE.Color('#0b1116'),
            wood: new THREE.Color('#7A5230'),
            stone: new THREE.Color('#3E3529'),
        }),
        []
    );

    // Procedural stucco bump map (tiny canvas) — makes walls read as textured, not flat
    const stuccoBump = useMemo(() => {
        const size = 256;
        const c = document.createElement('canvas');
        c.width = size; c.height = size;
        const ctx = c.getContext('2d');
        // base noise
        const img = ctx.createImageData(size, size);
        for (let i = 0; i < img.data.length; i += 4) {
            const n = 200 + Math.random() * 55;
            img.data[i] = n; img.data[i + 1] = n; img.data[i + 2] = n; img.data[i + 3] = 255;
        }
        ctx.putImageData(img, 0, 0);
        // subtle horizontal lines for troweled look
        ctx.globalAlpha = 0.15;
        ctx.fillStyle = '#000';
        for (let y = 0; y < size; y += 6) ctx.fillRect(0, y + (Math.random() * 2 | 0), size, 1);
        const tex = new THREE.CanvasTexture(c);
        tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
        tex.repeat.set(4, 3);
        tex.anisotropy = 8;
        return tex;
    }, []);

    const woodTexture = useMemo(() => {
        const size = 256;
        const c = document.createElement('canvas');
        c.width = size; c.height = size;
        const ctx = c.getContext('2d');
        ctx.fillStyle = '#6b4a2a';
        ctx.fillRect(0, 0, size, size);
        for (let i = 0; i < 80; i++) {
            ctx.strokeStyle = `rgba(${30 + Math.random() * 20}, ${20 + Math.random() * 12}, ${10 + Math.random() * 8}, 0.5)`;
            ctx.lineWidth = 0.5 + Math.random() * 1.2;
            ctx.beginPath();
            ctx.moveTo(0, Math.random() * size);
            ctx.bezierCurveTo(size * 0.3, Math.random() * size, size * 0.6, Math.random() * size, size, Math.random() * size);
            ctx.stroke();
        }
        const tex = new THREE.CanvasTexture(c);
        tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
        tex.repeat.set(2, 1);
        return tex;
    }, []);

    const concreteTexture = useMemo(() => {
        const size = 256;
        const c = document.createElement('canvas');
        c.width = size; c.height = size;
        const ctx = c.getContext('2d');
        const img = ctx.createImageData(size, size);
        for (let i = 0; i < img.data.length; i += 4) {
            const n = 78 + Math.random() * 25;
            img.data[i] = n; img.data[i + 1] = n - 4; img.data[i + 2] = n - 10; img.data[i + 3] = 255;
        }
        ctx.putImageData(img, 0, 0);
        const tex = new THREE.CanvasTexture(c);
        tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
        tex.repeat.set(4, 6);
        return tex;
    }, []);

    // All animation is driven via setProgress ref-handler from the parent scene driver.

    // Window helper
    const Win = ({ position, size = [0.7, 1.1, 0.08], rotation, idx }) => (
        <group position={position} rotation={rotation}>
            {/* Recess */}
            <mesh position={[0, 0, -0.04]}>
                <boxGeometry args={[size[0] + 0.08, size[1] + 0.08, 0.08]} />
                <meshStandardMaterial color="#0a0a0a" roughness={0.9} />
            </mesh>
            {/* Glass */}
            <mesh ref={(m) => (windowRefs.current[idx] = m)}>
                <boxGeometry args={size} />
                <meshStandardMaterial
                    color={C.glassWeathered}
                    emissive="#E8AC65"
                    emissiveIntensity={0.05}
                    roughness={0.12}
                    metalness={0.55}
                    envMapIntensity={0.6}
                />
            </mesh>
            {/* Frame */}
            <mesh position={[0, 0, 0.045]}>
                <boxGeometry args={[size[0] + 0.03, size[1] + 0.03, 0.02]} />
                <meshStandardMaterial color="#0a0a0a" roughness={0.4} metalness={0.35} />
            </mesh>
            {/* Mullion */}
            <mesh position={[0, 0, 0.06]}>
                <boxGeometry args={[0.02, size[1], 0.015]} />
                <meshStandardMaterial color="#0a0a0a" roughness={0.4} metalness={0.4} />
            </mesh>
        </group>
    );

    return (
        <group name="msd-house" position={[0, 0, 0]}>
            {/* WALLS - main volume */}
            <group name="walls">
                <RoundedBox
                    ref={(m) => (stuccoRefs.current[0] = m)}
                    args={[6.2, 2.4, 4.2]}
                    radius={0.06}
                    smoothness={3}
                    position={[0, 1.2, 0]}
                    castShadow
                    receiveShadow
                >
                    <meshStandardMaterial
                        color={C.stuccoWeathered}
                        roughness={0.95}
                        bumpMap={stuccoBump}
                        bumpScale={0.08}
                    />
                </RoundedBox>
                <RoundedBox
                    ref={(m) => (stuccoRefs.current[1] = m)}
                    args={[2.2, 1.8, 3.4]}
                    radius={0.06}
                    smoothness={3}
                    position={[-4.1, 0.9, 0.4]}
                    castShadow
                    receiveShadow
                >
                    <meshStandardMaterial color={C.stuccoWeathered} roughness={0.95} bumpMap={stuccoBump} bumpScale={0.08} />
                </RoundedBox>
                <RoundedBox
                    ref={(m) => (stuccoRefs.current[2] = m)}
                    args={[2.6, 2.0, 3.8]}
                    radius={0.06}
                    smoothness={3}
                    position={[4.4, 1.0, 0.2]}
                    castShadow
                    receiveShadow
                >
                    <meshStandardMaterial color={C.stuccoWeathered} roughness={0.95} bumpMap={stuccoBump} bumpScale={0.08} />
                </RoundedBox>

                {/* Chimney block for silhouette interest */}
                <RoundedBox args={[0.9, 1.4, 0.9]} radius={0.05} smoothness={3} position={[-1.6, 3.1, -1.5]} castShadow>
                    <meshStandardMaterial color={C.stuccoWeathered} roughness={0.95} bumpMap={stuccoBump} bumpScale={0.08} />
                </RoundedBox>
            </group>

            {/* PARAPET caps */}
            <group name="roof">
                <mesh ref={(m) => (roofRefs.current[0] = m)} position={[0, 2.46, 0]} castShadow receiveShadow>
                    <boxGeometry args={[6.35, 0.12, 4.35]} />
                    <meshStandardMaterial color={C.roofWeathered} roughness={0.75} metalness={0.05} />
                </mesh>
                <mesh ref={(m) => (roofRefs.current[1] = m)} position={[-4.1, 1.86, 0.4]} castShadow receiveShadow>
                    <boxGeometry args={[2.32, 0.12, 3.52]} />
                    <meshStandardMaterial color={C.roofWeathered} roughness={0.75} metalness={0.05} />
                </mesh>
                <mesh ref={(m) => (roofRefs.current[2] = m)} position={[4.4, 2.06, 0.2]} castShadow receiveShadow>
                    <boxGeometry args={[2.72, 0.12, 3.92]} />
                    <meshStandardMaterial color={C.roofWeathered} roughness={0.75} metalness={0.05} />
                </mesh>
            </group>

            {/* HORIZONTAL WOOD BAND (viga / trim accent) */}
            <group name="trim">
                <mesh ref={(m) => (trimRefs.current[0] = m)} position={[0, 2.35, 2.11]} castShadow>
                    <boxGeometry args={[6.3, 0.12, 0.08]} />
                    <meshStandardMaterial color={C.trimWeathered} roughness={0.5} />
                </mesh>
                {/* Wood accent panel by door */}
                <mesh position={[0.9, 0.9, 2.13]} castShadow>
                    <boxGeometry args={[1.4, 1.7, 0.04]} />
                    <meshStandardMaterial color={C.wood} roughness={0.55} map={woodTexture} />
                </mesh>
                {/* Vigas protruding (Southwestern signature) */}
                {[-2.3, -0.9, 0.5, 1.9].map((x, i) => (
                    <mesh key={i} position={[x, 2.35, 2.3]} rotation={[0, 0, 0]} castShadow>
                        <cylinderGeometry args={[0.09, 0.09, 0.55, 12]} />
                        <meshStandardMaterial color="#5a3d20" roughness={0.75} map={woodTexture} />
                        {/* Rotate so they point out horizontally */}
                        <group rotation={[Math.PI / 2, 0, 0]} />
                    </mesh>
                ))}
            </group>

            {/* Actually protrude vigas (correct rotation via group) */}
            <group name="vigas">
                {[-2.3, -0.9, 0.5, 1.9].map((x, i) => (
                    <group key={i} position={[x, 2.35, 2.3]} rotation={[Math.PI / 2, 0, 0]}>
                        <mesh castShadow>
                            <cylinderGeometry args={[0.08, 0.08, 0.5, 12]} />
                            <meshStandardMaterial color="#5a3d20" roughness={0.8} map={woodTexture} />
                        </mesh>
                    </group>
                ))}
            </group>

            {/* WINDOWS */}
            <group name="windows">
                <Win position={[-1.6, 1.4, 2.115]} size={[1.6, 1.2, 0.06]} idx={0} />
                <Win position={[-4.1, 1.15, 2.115]} size={[1.4, 1.1, 0.06]} idx={1} />
                <Win position={[4.4, 1.55, 2.115]} size={[0.55, 0.9, 0.06]} idx={2} />
                <Win position={[3.2, 1.2, 0.0]} rotation={[0, Math.PI / 2, 0]} size={[1.4, 0.8, 0.06]} idx={3} />
                <Win position={[-5.215, 1.2, 0.4]} rotation={[0, -Math.PI / 2, 0]} size={[1.6, 0.9, 0.06]} idx={4} />
                <Win position={[1.9, 1.55, 2.115]} size={[0.7, 1.1, 0.06]} idx={5} />
            </group>

            {/* FRONT DOOR with pivot */}
            <group name="front-door" position={[0.2, 0, 2.11]}>
                <group ref={doorPivotRef} position={[-0.45, 0, 0]}>
                    <RoundedBox
                        ref={doorPanelRef}
                        args={[0.9, 2.0, 0.08]}
                        radius={0.015}
                        smoothness={3}
                        position={[0.45, 1.0, 0.05]}
                        castShadow
                    >
                        <meshStandardMaterial color="#181614" roughness={0.35} metalness={0.15} />
                    </RoundedBox>
                    <mesh position={[0.78, 1.0, 0.11]}>
                        <sphereGeometry args={[0.04, 16, 16]} />
                        <meshStandardMaterial color="#c9b184" metalness={0.95} roughness={0.15} />
                    </mesh>
                </group>
                {/* Door frame */}
                <mesh position={[0, 1.0, 0.02]}>
                    <boxGeometry args={[1.05, 2.15, 0.04]} />
                    <meshStandardMaterial color="#0e0d0c" roughness={0.4} />
                </mesh>
            </group>

            {/* PORCH slab */}
            <mesh name="porch" position={[0, 0.05, 2.6]} receiveShadow castShadow>
                <boxGeometry args={[3.2, 0.14, 1.0]} />
                <meshStandardMaterial color="#3a342c" roughness={0.9} map={concreteTexture} />
            </mesh>

            {/* COLUMNS + overhang */}
            <group name="columns">
                {[-1.35, 1.35].map((x, i) => (
                    <mesh key={i} position={[x, 1.1, 3.05]} castShadow>
                        <boxGeometry args={[0.22, 2.2, 0.22]} />
                        <meshStandardMaterial color="#141312" roughness={0.55} />
                    </mesh>
                ))}
                <mesh position={[0, 2.22, 3.0]} castShadow>
                    <boxGeometry args={[3.2, 0.16, 0.95]} />
                    <meshStandardMaterial color="#141312" roughness={0.55} />
                </mesh>
            </group>

            {/* GARAGE DOOR — recessed */}
            <group name="garage-door" position={[4.4, 0.85, 2.11]}>
                <mesh position={[0, 0, -0.03]}>
                    <boxGeometry args={[2.3, 1.7, 0.04]} />
                    <meshStandardMaterial color="#0a0a0a" roughness={0.9} />
                </mesh>
                <mesh ref={garageRef} castShadow>
                    <boxGeometry args={[2.2, 1.6, 0.06]} />
                    <meshStandardMaterial color={C.garageWeathered} roughness={0.45} metalness={0.2} />
                </mesh>
                {/* Panel lines */}
                {[-0.4, 0, 0.4].map((y, i) => (
                    <mesh key={i} position={[0, y, 0.032]}>
                        <boxGeometry args={[2.2, 0.02, 0.005]} />
                        <meshStandardMaterial color="#000" roughness={0.7} />
                    </mesh>
                ))}
            </group>

            {/* STONE base accent */}
            <mesh name="stone-base" position={[-4.1, 0.18, 2.12]} receiveShadow castShadow>
                <boxGeometry args={[2.2, 0.36, 0.1]} />
                <meshStandardMaterial color={C.stone} roughness={0.95} bumpMap={stuccoBump} bumpScale={0.15} />
            </mesh>

            {/* WALKWAY */}
            <mesh name="walkway" position={[0, 0.015, 4.6]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
                <planeGeometry args={[1.6, 3.8]} />
                <meshStandardMaterial color="#4d4a42" roughness={0.9} map={concreteTexture} />
            </mesh>

            {/* DRIVEWAY */}
            <mesh name="driveway" position={[4.4, 0.012, 5.5]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
                <planeGeometry args={[3.4, 5.8]} />
                <meshStandardMaterial color="#3f3c35" roughness={0.9} map={concreteTexture} />
            </mesh>

            {/* WARM ARCHITECTURAL LIGHTS */}
            <group name="architectural-lights">
                <pointLight ref={(l) => (archLightRefs.current[0] = l)} position={[0, 2.05, 2.5]} color="#E8AC65" distance={5} decay={2} />
                <pointLight ref={(l) => (archLightRefs.current[1] = l)} position={[4.4, 2.0, 2.5]} color="#E8AC65" distance={4.2} decay={2} />
                {/* Sconces (visible fixtures) */}
                <mesh position={[-1.8, 1.9, 2.13]} castShadow>
                    <boxGeometry args={[0.06, 0.22, 0.06]} />
                    <meshStandardMaterial color="#161514" emissive="#E8AC65" emissiveIntensity={0.4} roughness={0.45} />
                </mesh>
                <mesh position={[2.2, 1.9, 2.13]} castShadow>
                    <boxGeometry args={[0.06, 0.22, 0.06]} />
                    <meshStandardMaterial color="#161514" emissive="#E8AC65" emissiveIntensity={0.4} roughness={0.45} />
                </mesh>
            </group>
        </group>
    );
});
House.displayName = 'House';
